import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Inbox, X, RotateCcw } from "lucide-react";

export function AppealsQueue() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null); // { appeal, action }
  const [note, setNote] = useState("");
  const load = () => api.get("/admin/appeals").then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post(`/admin/appeals/${target.appeal.id}/resolve`, { action: target.action, note: note.trim() });
      toast.success(target.action === "reinstate" ? "Account reinstated & user notified" : "Appeal rejected");
      setTarget(null); setNote(""); load();
    } catch (e) { toast.error(errText(e)); }
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  if (!data.appeals.length) return (
    <div className="text-center py-20 bg-white rounded-2xl border border-slate-200"><Inbox size={40} className="mx-auto text-slate-300" /><p className="text-slate-500 mt-3">No appeals yet. Suspended users can submit one from the sign-in screen.</p></div>
  );

  const badge = (s) => {
    const map = { open: "bg-amber-100 text-amber-700", reinstated: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${map[s] || map.open}`}>{s}</span>;
  };

  return (
    <div className="space-y-3" data-testid="appeals-list">
      {data.appeals.map((a) => (
        <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`appeal-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/seller/${a.user_id}`} className="font-semibold text-[#12234A] hover:text-[#F5551E]">{a.name}</Link>
                {badge(a.status)}
                <span className="text-xs text-slate-400">{a.email}</span>
              </div>
              <p className="text-sm text-slate-600 mt-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 whitespace-pre-wrap">{a.message}</p>
              <p className="text-xs text-slate-400 mt-2">Submitted {new Date(a.created_at).toLocaleString()}{a.resolved_by && ` · ${a.status} by ${a.resolved_by}`}</p>
              {a.resolution_note && <p className="text-xs text-slate-500 mt-1">Staff note: "{a.resolution_note}"</p>}
            </div>
            {a.status === "open" && (
              <div className="flex gap-2 shrink-0">
                <Button onClick={() => { setTarget({ appeal: a, action: "reject" }); setNote(""); }} variant="outline" data-testid={`appeal-reject-${a.id}`} className="rounded-xl gap-1.5"><X size={15} /> Reject</Button>
                <Button onClick={() => { setTarget({ appeal: a, action: "reinstate" }); setNote(""); }} data-testid={`appeal-reinstate-${a.id}`} className="rounded-xl gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"><RotateCcw size={15} /> Reinstate</Button>
              </div>
            )}
          </div>
        </div>
      ))}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="appeal-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A]">{target?.action === "reinstate" ? "Reinstate account" : "Reject appeal"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {target?.action === "reinstate"
                ? "The user's account will be reactivated and their hidden ads restored. They'll get a message with your note."
                : "The account stays suspended. Add an optional internal note."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="appeal-note" rows={3} maxLength={500}
            placeholder={target?.action === "reinstate" ? "Note to the user (optional)…" : "Internal note (optional)…"} className="rounded-xl resize-none" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submit} data-testid="appeal-resolve-confirm" className={`rounded-xl text-white ${target?.action === "reinstate" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
              {target?.action === "reinstate" ? "Reinstate" : "Reject appeal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
